<script setup lang="ts">
import {ref, reactive, computed, onMounted, onUnmounted, useTemplateRef} from 'vue';
import {SvgIcon} from '../../svg.ts';
import {GET} from '../../modules/fetch.ts';
import {generateAriaId} from '../../modules/fomantic/base.ts';
import {formatDatetime as fmtDatetime} from '../../utils/time.ts';
import {buildApplyUrl, toggleCommitSelection} from './commit-selection.ts';

type CommitInfo = {
  id: string;
  short_sha: string;
  summary: string;
  committer_or_author_name: string;
  time: string;
  checked: boolean;
};

type SelectionMode = 'all' | 'sinceReview' | 'custom';

// Read initial state from the hidden data-carrier element
const el = document.querySelector('#diff-commit-select')!;
const queryParams = el.getAttribute('data-queryparams') ?? '';
const issueLink = el.getAttribute('data-issuelink') ?? '';
const isShowingAllCommits = el.getAttribute('data-is-showing-all-commits') === 'true';
const isShowingSingleCommit = el.getAttribute('data-is-showing-single-commit') === 'true';
const afterCommitId = el.getAttribute('data-after-commit-id') || '';
// beforeCommitId is the exclusive start of the range (i.e. the commit *before*
// the changes shown), not the commit where changes begin.
const beforeCommitId = el.getAttribute('data-before-commit-id') || '';
const mergeBase = el.getAttribute('data-merge-base') || '';

const panelVisible = ref(false);
const isLoading = ref(false);
const commits = ref<CommitInfo[]>([]);
const lastReviewCommitSha = ref<string | null>(null);
const selectionMode = ref<SelectionMode>('all');
const locale = reactive<Record<string, string>>({
  filter_changes_by_commit: el.getAttribute('data-filter_changes_by_commit') ?? '',
});
const uniqueIdPanel = generateAriaId();

const rootEl = useTemplateRef<HTMLElement>('rootEl');

// -- Computed --

const commitsSinceLastReview = computed(() => {
  if (lastReviewCommitSha.value) {
    return commits.value.length - commits.value.findIndex((x) => x.id === lastReviewCommitSha.value) - 1;
  }
  return 0;
});

const checkedCount = computed(() => commits.value.filter((c) => c.checked).length);

const buttonLabel = computed(() => {
  if (commits.value.length && selectionMode.value === 'custom') {
    const checked = checkedCount.value;
    if (checked > 0 && checked < commits.value.length) {
      return (locale.filter_commits_button_count || '%s of %s Commits')
        .replace('%s', String(checked))
        .replace('%s', String(commits.value.length));
    }
  }
  return locale.filter_changes_by_commit || 'Filter Commits';
});

const applyUrl = computed(() => buildApplyUrl({
  issueLink,
  queryParams,
  selectionMode: selectionMode.value,
  lastReviewCommitSha: lastReviewCommitSha.value,
  commits: commits.value,
  mergeBase,
}));

// -- Methods --

async function fetchCommits() {
  const resp = await GET(`${issueLink}/commits/list`);
  const results = await resp.json();
  commits.value = results.commits.map((x: CommitInfo) => {
    x.checked = false;
    return x;
  }).reverse();
  lastReviewCommitSha.value = results.last_review_commit_sha || null;
  if (lastReviewCommitSha.value && !commits.value.some((x) => x.id === lastReviewCommitSha.value)) {
    lastReviewCommitSha.value = null;
  }
  Object.assign(locale, results.locale);
}

function initSelectionFromCurrentState() {
  for (const c of commits.value) {
    c.checked = false;
  }
  if (isShowingAllCommits) {
    selectionMode.value = 'all';
    return;
  }
  if (lastReviewCommitSha.value && beforeCommitId === lastReviewCommitSha.value) {
    selectionMode.value = 'sinceReview';
    return;
  }
  selectionMode.value = 'custom';
  if (isShowingSingleCommit && afterCommitId) {
    const idx = commits.value.findIndex((c) => c.id === afterCommitId);
    if (idx !== -1) commits.value[idx].checked = true;
  } else if (beforeCommitId && afterCommitId) {
    const beforeIdx = commits.value.findIndex((c) => c.id === beforeCommitId);
    const afterIdx = commits.value.findIndex((c) => c.id === afterCommitId);
    if (afterIdx !== -1) {
      const startIdx = beforeIdx === -1 ? 0 : beforeIdx + 1;
      for (let i = startIdx; i <= afterIdx; i++) {
        commits.value[i].checked = true;
      }
    }
  } else if (afterCommitId) {
    const afterIdx = commits.value.findIndex((c) => c.id === afterCommitId);
    if (afterIdx !== -1) {
      for (let i = 0; i <= afterIdx; i++) {
        commits.value[i].checked = true;
      }
    }
  }
}

function onKeyDown(event: KeyboardEvent) {
  if (!panelVisible.value) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    closePanel();
  }
}

async function togglePanel() {
  if (panelVisible.value) {
    closePanel();
    return;
  }
  panelVisible.value = true;
  if (!commits.value.length && !isLoading.value) {
    isLoading.value = true;
    try {
      await fetchCommits();
      initSelectionFromCurrentState();
    } finally {
      isLoading.value = false;
    }
  }
}

function closePanel() {
  panelVisible.value = false;
  if (commits.value.length) {
    initSelectionFromCurrentState();
  }
}

function selectMode(mode: SelectionMode) {
  selectionMode.value = mode;
  if (mode !== 'custom') {
    for (const c of commits.value) {
      c.checked = false;
    }
  }
}

function toggleCommit(idx: number) {
  selectionMode.value = toggleCommitSelection(commits.value, idx) as SelectionMode;
}

function apply() {
  window.location.assign(applyUrl.value);
}

function formatDatetime(isoString: string): string {
  return fmtDatetime(new Date(isoString));
}

// -- Lifecycle --

onMounted(async () => {
  rootEl.value?.addEventListener('keydown', onKeyDown);
  if (!isShowingAllCommits) {
    isLoading.value = true;
    try {
      await fetchCommits();
      initSelectionFromCurrentState();
    } finally {
      isLoading.value = false;
    }
  }
});

onUnmounted(() => {
  rootEl.value?.removeEventListener('keydown', onKeyDown);
});
</script>
<template>
  <div ref="rootEl" class="diff-commit-selector">
    <button
      class="ui basic button diff-commit-selector-btn"
      @click.stop="togglePanel()"
      aria-haspopup="true"
      :aria-label="locale.filter_changes_by_commit"
      :aria-controls="uniqueIdPanel"
      :aria-expanded="panelVisible ? 'true' : 'false'"
    >
      <SvgIcon name="octicon-git-commit"/>
      <span class="tw:ml-1">{{ buttonLabel }}</span>
      <SvgIcon name="octicon-triangle-down" :size="14" class="tw:ml-1"/>
    </button>

    <!-- Anchored panel -->
    <div
      v-show="panelVisible"
      v-cloak
      :id="uniqueIdPanel"
      class="commit-filter-panel"
      @click.stop
    >
      <!-- Loading state -->
      <div class="loading-indicator is-loading" v-if="isLoading"/>

      <template v-if="!isLoading && commits.length">
        <!-- Radio options section -->
        <div class="commit-filter-section">
          <label class="commit-filter-radio" @click="selectMode('all')">
            <input type="radio" name="commit-filter-mode" value="all" :checked="selectionMode === 'all'">
            <div class="tw:flex-1">
              <div>{{ locale.show_all_commits }}</div>
              <div class="text light-2 tw:text-xs">{{ locale.stats_num_commits }}</div>
            </div>
          </label>
          <label
            v-if="lastReviewCommitSha != null"
            class="commit-filter-radio"
            :class="{disabled: !commitsSinceLastReview}"
            @click="commitsSinceLastReview ? selectMode('sinceReview') : null"
          >
            <input type="radio" name="commit-filter-mode" value="sinceReview" :checked="selectionMode === 'sinceReview'" :disabled="!commitsSinceLastReview">
            <div class="tw:flex-1">
              <div>{{ locale.show_changes_since_your_last_review }}</div>
              <div class="text light-2 tw:text-xs">{{ commitsSinceLastReview }} commits</div>
            </div>
          </label>
        </div>

        <!-- Helper text -->
        <div class="commit-filter-helper text light-2">
          {{ locale.select_commit_hold_shift_for_range }}
        </div>

        <!-- Commit list -->
        <div class="commit-filter-list">
          <label
            v-for="(commit, idx) in commits"
            :key="commit.id"
            class="commit-filter-item"
            :class="{checked: commit.checked}"
          >
            <input
              type="checkbox"
              :checked="commit.checked"
              @change="toggleCommit(idx)"
            >
            <div class="tw:flex-1 tw:flex tw:flex-col tw:gap-0.5 tw:min-w-0">
              <div class="gt-ellipsis commit-list-summary">
                {{ commit.summary }}
              </div>
              <div class="gt-ellipsis text light-2 tw:text-xs">
                {{ commit.committer_or_author_name }}
                <span class="tw:ml-2">{{ formatDatetime(commit.time) }}</span>
              </div>
            </div>
            <div class="tw:font-mono tw:text-xs tw:opacity-50 tw:self-start tw:pt-0.5">
              {{ commit.short_sha }}
            </div>
          </label>
        </div>

        <!-- Footer with Cancel/Apply -->
        <div class="commit-filter-footer">
          <button class="ui basic small button" @click="closePanel()">
            {{ locale.cancel || 'Cancel' }}
          </button>
          <button class="ui primary small button" @click="apply()">
            {{ locale.apply || 'Apply' }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>
<style scoped>
  .diff-commit-selector {
    position: relative;
  }

  .diff-commit-selector-btn {
    align-items: center;
    gap: 0;
    white-space: nowrap;
  }

  .commit-filter-panel {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 100;
    width: 440px;
    max-width: 96vw;
    background: var(--color-body);
    border: 1px solid var(--color-secondary);
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
  }

  .commit-filter-panel .loading-indicator {
    height: 200px;
    width: 100%;
  }

  /* Radio options section */
  .commit-filter-section {
    padding: 8px 0;
    border-bottom: 1px solid var(--color-secondary);
  }

  .commit-filter-radio {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 6px 14px;
    cursor: pointer;
    margin: 0;
  }

  .commit-filter-radio:hover {
    background: var(--color-hover);
  }

  .commit-filter-radio.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .commit-filter-radio input[type="radio"] {
    margin-top: 3px;
    flex-shrink: 0;
  }

  /* Helper text */
  .commit-filter-helper {
    padding: 8px 14px;
    font-size: 12px;
    border-bottom: 1px solid var(--color-secondary);
  }

  /* Scrollable commit list */
  .commit-filter-list {
    overflow-y: auto;
    max-height: 350px;
  }

  .commit-filter-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 14px;
    cursor: pointer;
    border-left: 3px solid transparent;
    margin: 0;
    transition: border-color 0.1s, background-color 0.1s;
  }

  .commit-filter-item:not(:last-child) {
    border-bottom: 1px solid var(--color-secondary);
  }

  .commit-filter-item:hover {
    background: var(--color-hover);
  }

  .commit-filter-item.checked {
    border-left-color: var(--color-primary);
    background: var(--color-small-accent);
  }

  .commit-filter-item input[type="checkbox"] {
    margin-top: 3px;
    flex-shrink: 0;
  }

  .commit-filter-item .commit-list-summary {
    max-width: min(340px, 70vw);
  }

  /* Footer */
  .commit-filter-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 14px;
    border-top: 1px solid var(--color-secondary);
  }
</style>
